import React from "react"

import { Clock, MapPin, Truck } from "lucide-react"

import CostBreakdownCard from "@/components/shared/CostBreakdownCard"
import { Badge } from "@/components/ui/badge"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { formatCost } from "@/lib/routeCostCalculation"
import { RouteWeeklyData } from "@/lib/weeklyPlan"

import { formatDurationHm, stopTypeLabel } from "./format"

export interface RouteDetailSelection {
  route: RouteWeeklyData
  dateStr: string
  dayLabel: string
}

interface RouteDetailSheetProps {
  selection: RouteDetailSelection | null
  onClose: () => void
}

const RouteDetailSheet: React.FC<RouteDetailSheetProps> = ({ selection, onClose }) => {
  if (!selection) return null
  const { route, dateStr, dayLabel } = selection
  const detail = route.dayCosts[dateStr]
  const cb = detail?.costBreakdown

  return (
    <Sheet
      open={!!selection}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="truncate" title={route.code}>
            {route.code}
          </SheetTitle>
          <p className="text-sm text-palette-jungleGreen500">
            {dayLabel} — {dateStr}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs">
              {route.category}
            </Badge>
            <Badge variant="outline" className="text-xs capitalize">
              {route.status}
            </Badge>
            {detail?.vehicleType && (
              <Badge variant="secondary" className="text-xs gap-1">
                <Truck className="h-3 w-3" />
                {detail.vehicleType}
              </Badge>
            )}
          </div>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {(detail?.durationMinutes != null || detail?.distanceMiles != null) && (
            <div className="grid grid-cols-2 gap-3">
              {detail?.durationMinutes != null && (
                <div className="flex items-center justify-between p-2 bg-white/60 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-purple-100 rounded-md">
                      <Clock size={12} className="text-purple-600" />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-palette-jungleGreen900">
                        {formatDurationHm(detail.durationMinutes)}
                      </div>
                      <div className="text-[10px] text-palette-jungleGreen500">Duration</div>
                    </div>
                  </div>
                </div>
              )}
              {detail?.distanceMiles != null && (
                <div className="flex items-center justify-between p-2 bg-white/60 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-green-100 rounded-md">
                      <MapPin size={12} className="text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-palette-jungleGreen900">
                        {detail.distanceMiles.toFixed(1)} mi
                      </div>
                      <div className="text-[10px] text-palette-jungleGreen500">Distance</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {cb && (
            <CostBreakdownCard
              density="full"
              title="Estimated Route Cost"
              subtitle="Based on current configuration"
              lines={[
                { kind: "lease", label: "Vehicle Lease", value: cb.vehicle_lease_cost },
                {
                  kind: "charging",
                  label: "Charging Cost",
                  detail:
                    detail.distanceMiles != null && detail.distanceMiles > 0
                      ? `${detail.distanceMiles.toFixed(1)} miles`
                      : null,
                  value: cb.charging_cost > 0 ? cb.charging_cost : null,
                },
                {
                  kind: "wage",
                  label: "Driver Wage",
                  detail:
                    detail.durationMinutes != null && detail.durationMinutes > 0
                      ? formatDurationHm(detail.durationMinutes)
                      : null,
                  value: cb.driver_wage_cost > 0 ? cb.driver_wage_cost : null,
                },
              ]}
              total={cb.total_cost}
              revenue={cb.total_haulage_delivery_revenue}
            />
          )}

          {detail?.stops && detail.stops.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold text-palette-jungleGreen500 uppercase tracking-wide mb-2">
                Stops ({detail.stops.length})
              </h4>
              {(detail?.startTime || detail?.endTime) && (
                <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-blue-50 border border-blue-100 text-xs">
                  <Clock size={14} className="text-blue-600 flex-shrink-0" />
                  <span className="font-medium text-palette-jungleGreen900">
                    {detail.startTime ?? "—"} — {detail.endTime ?? "—"}
                  </span>
                </div>
              )}
              <div className="space-y-1.5">
                {[...detail.stops]
                  .sort((a, b) => a.sequence - b.sequence)
                  .map((stop, i) => (
                    <div
                      key={stop.id || i}
                      className="flex items-start gap-2.5 rounded-md border border-palette-jungleGreen100 px-3 py-2 bg-white hover:bg-palette-jungleGreen000 transition-colors"
                    >
                      <div className="flex flex-col items-center mt-0.5">
                        <div className="w-5 h-5 rounded-full bg-palette-jungleGreen100 flex items-center justify-center text-[10px] font-bold text-palette-jungleGreen700">
                          {stop.sequence}
                        </div>
                        {i < detail.stops!.length - 1 && (
                          <div className="w-px h-3 bg-palette-jungleGreen200 mt-0.5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-medium text-palette-jungleGreen800 truncate">
                            {stop.location?.site_name || "Unknown location"}
                          </span>
                          <Badge variant="outline" className="text-[9px] py-0 px-1 flex-shrink-0">
                            {stopTypeLabel(stop.stop_type)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-palette-jungleGreen400">
                          <span>
                            {stop.planned_arrival} — {stop.planned_departure}
                          </span>
                          <span>·</span>
                          <span>{stop.service_time_minutes}m service</span>
                        </div>
                        {stop.location?.address && (
                          <div className="text-[10px] text-palette-jungleGreen400 truncate mt-0.5">
                            {stop.location.address}
                          </div>
                        )}
                        {stop.haulage_delivery_revenue_gbp != null &&
                          stop.haulage_delivery_revenue_gbp > 0 && (
                            <div className="text-[10px] text-green-600 mt-0.5">
                              Revenue: {formatCost(stop.haulage_delivery_revenue_gbp)}
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          )}

          {!detail?.stops && cb && (
            <p className="text-xs text-palette-jungleGreen400 italic">
              Stop details not available for locked snapshots.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default RouteDetailSheet
